import React from 'react';
import { FileText, Check } from 'lucide-react';
import { Button } from './Button';
import styles from './CommunityGuidelinesModal.module.css';

interface CommunityGuidelinesModalProps {
  isOpen: boolean;
  onAccept?: () => void;
  onClose?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
}

export const CommunityGuidelinesModal: React.FC<CommunityGuidelinesModalProps> = ({
  isOpen,
  onAccept,
  onClose,
  readOnly = false,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (readOnly && onClose) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <FileText size={28} />
          </div>
          <h2 className={styles.title}>Matasma Collective Principles</h2>
          <p className={styles.subtitle}>
            {readOnly
              ? 'Our principles for a positive workplace community'
              : 'Please review and accept our collective principles to continue'}
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.section}>
            <h3>1. Respectful Communication</h3>
            <p>
              Treat all colleagues with respect and professionalism. Use inclusive language
              and maintain a positive tone in all interactions. Avoid offensive, discriminatory,
              or inappropriate content.
            </p>
          </div>

          <div className={styles.section}>
            <h3>2. Authentic Recognition</h3>
            <p>
              Give genuine appreciation that reflects real contributions and achievements.
              Be specific about what you're recognizing and why it matters. Avoid excessive
              or insincere recognition that diminishes its value.
            </p>
          </div>

          <div className={styles.section}>
            <h3>3. Privacy & Confidentiality</h3>
            <p>
              Respect the privacy of your colleagues. Do not share personal information,
              confidential business data, or sensitive details without proper authorization.
              Be mindful of what you post publicly.
            </p>
          </div>

          <div className={styles.section}>
            <h3>4. No Harassment or Discrimination</h3>
            <p>
              Harassment, bullying, or discrimination of any kind is strictly prohibited.
              This includes but is not limited to comments based on race, gender, religion,
              age, disability, sexual orientation, or any other protected characteristic.
            </p>
          </div>

          <div className={styles.section}>
            <h3>5. Appropriate Content Standards</h3>
            <p>
              Keep all content professional and work-appropriate. Do not post offensive
              images, inappropriate language, or content that could create a hostile
              work environment. When in doubt, err on the side of caution.
            </p>
          </div>

          <div className={styles.section}>
            <h3>6. Reporting Violations</h3>
            <p>
              If you witness any violation of these guidelines, please report it to your
              administrator or HR representative immediately. Reports will be handled
              confidentially and investigated thoroughly.
            </p>
          </div>

          <div className={styles.notice}>
            <p>
              <strong>Note:</strong> Violations of these principles may result in disciplinary
              action, including removal of platform privileges. By using RIPPLE, you agree
              to uphold these standards and contribute to a positive workplace culture.
            </p>
          </div>
        </div>

        <div className={styles.footer}>
          {readOnly ? (
            <Button onClick={onClose} fullWidth>
              Close
            </Button>
          ) : (
            <Button
              onClick={onAccept}
              icon={<Check size={18} />}
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? 'Accepting...' : 'I Accept These Principles'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
